import { useState } from 'react';
import { getLoginFlowError } from '@/shared/utils';

/** Discriminated result so a `void`-returning start call (email) is still distinguishable from failure. */
export type StepUpResult<T> = { ok: true; value: T } | { ok: false };

/**
 * Shared current-password step-up in front of every factor-enrollment start call (TOTP, email,
 * passkey). Owns the password field, the in-flight flag, and the inline error. `runStepUp` hands the
 * collected password to whichever start call the caller supplies: on rejection it surfaces the error
 * and keeps the prompt open (`ok: false`); on success it clears the password — never retained past the
 * request — and returns the start result. Nothing here persists the password beyond a single call.
 */
export const useStepUp = () => {
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runStepUp = async <T>(start: (currentPassword: string) => Promise<T>): Promise<StepUpResult<T>> => {
    setError(null);
    setIsVerifying(true);

    try {
      const value = await start(password);
      // Used only for this request; drop it immediately so it is not held in state afterward.
      setPassword('');

      return { ok: true, value };
    } catch (caught) {
      setError(getLoginFlowError(caught));

      return { ok: false };
    } finally {
      setIsVerifying(false);
    }
  };

  const reset = () => {
    setPassword('');
    setError(null);
    setIsVerifying(false);
  };

  return {
    password,
    setPassword,
    isVerifying,
    error,
    clearError: () => setError(null),
    runStepUp,
    reset,
  };
};
